import { BUY_X_PAY_Y_LIMITS, CAMPAIGN_CUSTOMER_KIND_LABELS, CAMPAIGN_CUSTOMER_KINDS, CAMPAIGN_EFFECT_TYPE_LABELS, CAMPAIGN_EFFECT_TYPES, CAMPAIGN_CONTEXT_FLAG_LABELS, CAMPAIGN_CONTEXT_FLAGS, CAMPAIGN_SCOPE_KINDS, campaignBuyQuantitySchema, campaignCrossConditionSchema, campaignCustomerKindSchema, campaignEffectTypeSchema, campaignLoyaltyTierSchema, campaignObjectSchema, campaignPayQuantitySchema, campaignPositiveKurusSchema, campaignRuleIssues, campaignSchema, campaignScopeKindSchema, LOYALTY_TIER_LIMITS, withCampaignRules, } from "@station/pricing-engine";
import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { timestampSchema, uuidSchema } from "../common/primitives.js";
/**
 * Kampanya yönetimi uçlarının sözleşmesi (Prompt 15; Prompt 16'da X al Y öde / sepet eşiği / sadakat seviyesi;
 * Prompt 17'de kupon kampanyası ve çapraz koşul; izin `pos.manage`).
 *
 * **Kampanya ŞEMASI burada yeniden tanımlanmaz:** tanım `@station/pricing-engine`'dedir (`campaignSchema`) ve
 * motor da POS da AYNI şemayı kullanır. Burada yalnızca API zarfı (kapsam, sayfalama, sürüm) vardır. Çapraz alan
 * kuralları (`BUY_X_PAY_Y` ⇔ X + Y, `LOYALTY_TIER` ⇔ seviye) türetilen şemalara `withCampaignRules` ile yeniden bağlanır.
 */
export { BUY_X_PAY_Y_LIMITS, CAMPAIGN_CONTEXT_FLAG_LABELS, CAMPAIGN_CONTEXT_FLAGS, campaignCrossConditionSchema, CAMPAIGN_CUSTOMER_KIND_LABELS, CAMPAIGN_CUSTOMER_KINDS, CAMPAIGN_EFFECT_TYPE_LABELS, CAMPAIGN_EFFECT_TYPES, CAMPAIGN_SCOPE_KINDS, campaignCustomerKindSchema, campaignEffectTypeSchema, campaignRuleIssues, campaignSchema, campaignScopeKindSchema, LOYALTY_TIER_LIMITS, };
export const CAMPAIGN_SCOPE_KIND_LABELS = {
    PRODUCT: "Seçili ürünler",
    CATEGORY: "Ürün kategorisi",
    BUSINESS_UNIT: "İş birimi",
};
/** API kaydı: motor şeması + şirket kapsamı, denetim ve iyimser kilit alanları. */
export const campaignRecordSchema = withCampaignRules(campaignObjectSchema.extend({
    companyId: uuidSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int().positive(),
}));
export const campaignPageSchema = pageOf(campaignRecordSchema);
/**
 * Kimlik sunucuda üretilir; `isActive` oluşturmada her zaman true'dur (kapatma ayrı uçtur).
 * `BUY_X_PAY_Y`'de `effectValue` motor tarafından kullanılmaz; sunucu onu "X/Y" okunur aynasıyla yazar.
 */
const campaignInputFields = campaignObjectSchema.omit({ id: true, isActive: true });
export const createCampaignSchema = withCampaignRules(campaignInputFields.extend({ companyId: uuidSchema }));
/**
 * Kısmi güncelleme. `code` ve `companyId` DEĞİŞTİRİLEMEZ (kampanya kimliği fişlerde ve satırlarda durur).
 * Not: `patchOf` yerine elle `partial()` kullanılamaz — varsayılanlar sessiz veri kaybı yapar (`common/patch.ts`).
 *
 * Çapraz alan kuralı yama üzerinde DEĞİL, sunucuda BİRLEŞTİRİLMİŞ kayıt (eski + yama) üzerinde sınanır
 * (`campaignRuleIssues`). Etki türü `BUY_X_PAY_Y`'den başka bir türe çekilir ve yama X / Y'ye değinmezse sunucu
 * onları boşaltır; müşteri türü `LOYALTY_TIER`'dan çıkarılırsa seviyeler de aynı biçimde boşaltılır.
 */
export const updateCampaignSchema = z
    .object({
    name: campaignSchema.shape.name.optional(),
    priority: z.number().int().min(0).max(1000).optional(),
    scopeKind: campaignScopeKindSchema.optional(),
    scopeIds: z.array(z.string().min(1)).min(1).max(500).optional(),
    stationIds: z.array(uuidSchema).optional(),
    effectType: campaignEffectTypeSchema.optional(),
    effectValue: z.string().min(1).max(30).optional(),
    buyQuantity: campaignBuyQuantitySchema.nullable().optional(),
    payQuantity: campaignPayQuantitySchema.nullable().optional(),
    minBasketGross: campaignPositiveKurusSchema.nullable().optional(),
    minQuantity: z
        .string()
        .regex(/^\d{1,15}(\.\d{1,3})?$/)
        .nullable()
        .optional(),
    minLineGross: z
        .string()
        .regex(/^\d{1,15}$/)
        .nullable()
        .optional(),
    customerKind: campaignCustomerKindSchema.optional(),
    loyaltyTiers: z.array(campaignLoyaltyTierSchema).max(LOYALTY_TIER_LIMITS.maxTiers).optional(),
    /** Prompt 17 (ADR 0017 §23): kupon kampanyası. */
    requiresCoupon: z.boolean().optional(),
    /** Prompt 17 (ADR 0017 §26): çapraz koşul; `null` kaldırır. */
    crossCondition: campaignCrossConditionSchema.nullable().optional(),
    validFrom: z.iso.datetime({ offset: true }).optional(),
    validTo: z.iso.datetime({ offset: true }).nullable().optional(),
    weekdays: z.array(z.number().int().min(0).max(6)).optional(),
    timeFrom: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .nullable()
        .optional(),
    timeTo: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .nullable()
        .optional(),
})
    .extend({ version: z.number().int().positive() });
export const deactivateCampaignSchema = z.object({ version: z.number().int().positive() });
export const listCampaignsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema
        .optional()
        .describe("Verilirse bu istasyonu KAPSAYAN kampanyalar (şirket geneli dâhil)"),
    isActive: z.enum(["true", "false"]).optional(),
    /** Yalnızca `validTo` geçmemiş olanlar. */
    currentOnly: z.enum(["true", "false"]).optional(),
    /** Prompt 17 (ADR 0017 §23): `true` → yalnızca "kupon ister" kampanyaları (kupon şablonu seçicisi); `false` → hariç. */
    requiresCoupon: z
        .enum(["true", "false"])
        .optional()
        .describe("true: yalnızca kupon isteyen kampanyalar; false: kupon istemeyenler"),
});
export const campaignParamsSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=campaigns.js.map